<template>
  <DefaultLayout>
    <div slot="content">
      <div class="d-flex align-center flex-wrap py-3">
        <div>
          <div class="title pb-1">Integration Settings</div>
          <div class="subtitle-2 grey--text text--darken-1">
            Manage the integrations that connect PlanetVerify to external platforms.
          </div>
        </div>
        <v-spacer></v-spacer>
        <v-btn depressed color="primary" class="my-2" @click="openFlowDialog">
          <v-icon left small>fal fa-plus</v-icon>
          Add Flow
        </v-btn>
      </div>

      <v-divider class="mb-6"></v-divider>

      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
        show-arrows
        slider-color="primary"
        class="mb-4"
      >
        <v-tab v-for="tab in tabs" :key="tab.value" :href="`#${tab.value}`">
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item value="signzy">
          <v-card outlined>
            <v-card-text>
              <div class="subtitle-1 font-weight-medium mb-1">Signzy orchestration flows</div>
              <div class="body-2 grey--text text--darken-1 mb-4">
                Use flows to determine how data is exchanged with Signzy environments.
              </div>

              <v-row class="mb-4">
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="search"
                    dense
                    outlined
                    append-icon="fal fa-search"
                    label="Search flows"
                    hide-details
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="8" class="text-md-right d-flex align-center justify-end">
                  <v-chip color="blue lighten-5" text-color="blue darken-2" class="ma-0" label small>
                    {{ signzyFlows.length }} active {{ signzyFlows.length === 1 ? 'flow' : 'flows' }}
                  </v-chip>
                </v-col>
              </v-row>

              <v-data-table
                :headers="signzyHeaders"
                :items="signzyFlows"
                :search="search"
                :items-per-page="5"
                class="elevation-0"
                :footer-props="dataTableFooter"
                :hide-default-footer="signzyFlows.length <= 5"
                :loading="dataLoading"
              >
                <template v-slot:item.flowId="{ item }">
                  <code>{{ item.flowId }}</code>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        color="grey darken-1"
                        :loading="deletingFlowId === item.flowId"
                        :disabled="deletingFlowId === item.flowId"
                        v-on="on"
                        @click="deleteFlow(item)"
                      >
                        <v-icon small>fal fa-trash</v-icon>
                      </v-btn>
                    </template>
                    <span>Remove flow</span>
                  </v-tooltip>
                </template>
                <template v-slot:loading>
                  <v-sheet class="text-center py-6" color="transparent">
                    <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                    <div class="body-2 mt-2">Loading Signzy flows…</div>
                  </v-sheet>
                </template>
                <template v-slot:no-data>
                  <v-alert type="info" text tile class="mb-0">
                    No flows found. Use the "Add Flow" button to create your first Signzy flow.
                  </v-alert>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <!-- <v-tab-item value="comingSoon">
          <v-card outlined>
            <v-card-text>
              <div class="subtitle-1 font-weight-medium mb-2">More integrations coming soon</div>
              <div class="body-2 grey--text text--darken-1">
                This area will display configuration options for additional partners as soon as they
                become available.
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item> -->
      </v-tabs-items>

      <v-dialog v-model="flowDialog" max-width="520px" persistent>
        <v-card>
          <v-card-title>Add Signzy flow</v-card-title>
          <v-card-text>
            <v-form ref="flowForm" lazy-validation>
              <v-text-field
                v-model="newFlow.flowName"
                label="Flow name"
                outlined
                dense
                :rules="[(v) => !!(v && v.trim()) || 'Name is required']"
              ></v-text-field>
              <v-text-field
                v-model="newFlow.flowId"
                label="Flow ID"
                outlined
                dense
                :rules="[(v) => !!(v && v.trim()) || 'Flow ID is required']"
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text tile color="grey darken-1" @click="closeFlowDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              tile
              depressed
              @click="saveFlow"
              :loading="savingFlow"
              :disabled="savingFlow"
            >
              Save flow
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </DefaultLayout>
</template>

<script>
import uuidv4 from 'uuid/v4';
import { mapActions } from 'vuex';
import DefaultLayout from '@/layouts/DefaultLayout';
import { GET_SIGNZY_FLOWS, ADD_SIGNZY_FLOW, DELETE_SIGNZY_FLOW } from '@/store/actions.type';

export default {
  name: 'integration-settings',
  components: {
    DefaultLayout
  },
  data() {
    return {
      activeTab: 'signzy',
      tabs: [
        { label: 'Signzy', value: 'signzy' },
        // { label: 'Coming soon', value: 'comingSoon' }
      ],
      signzyHeaders: [
        { text: 'Flow name', value: 'flowName', align: 'left' },
        { text: 'Flow ID', value: 'flowId', align: 'left' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      signzyFlows: [],
      search: '',
      flowDialog: false,
      newFlow: {
        flowName: '',
        flowId: ''
      },
      savingFlow: false,
      deletingFlowId: null,
      dataLoading: false,
      dataTableFooter: {
        itemsPerPageOptions: [5, 10, 20],
        prevIcon: 'fal fa-chevron-left',
        nextIcon: 'fal fa-chevron-right',
        itemsPerPageText: 'Rows per page'
      }
    };
  },
  created() {
    this.fetchSignzyFlows();
  },
  methods: {
    ...mapActions({
      requestSignzyFlows: GET_SIGNZY_FLOWS,
      createSignzyFlow: ADD_SIGNZY_FLOW,
      removeSignzyFlow: DELETE_SIGNZY_FLOW
    }),
    async fetchSignzyFlows() {
      this.dataLoading = true;
      try {
        const flows = await this.requestSignzyFlows();
        this.signzyFlows = this.normalizeFlows(flows);
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Unable to load flows',
          text: 'Please try again in a moment.'
        });
      } finally {
        this.dataLoading = false;
      }
    },
    normalizeFlows(apiResponse) {
      const flows = Array.isArray(apiResponse?.flows) ? apiResponse.flows : apiResponse || [];

      if (!Array.isArray(flows)) {
        return [];
      }

      return flows.map((flow) => ({
        id: flow.id || flow.flowId || uuidv4(),
        flowName: flow.flowName || flow.name || 'Untitled flow',
        flowId: flow.flowId || flow.id || uuidv4()
      }));
    },
    openFlowDialog() {
      this.resetFlowForm();
      this.flowDialog = true;
    },
    closeFlowDialog() {
      this.flowDialog = false;
    },
    resetFlowForm() {
      this.newFlow = {
        flowName: '',
        flowId: ''
      };
      if (this.$refs.flowForm) {
        this.$refs.flowForm.resetValidation();
      }
    },
    async saveFlow() {
      if (!this.$refs.flowForm || !this.$refs.flowForm.validate()) {
        return;
      }

      const generatedId = uuidv4();
      const flowName = this.newFlow.flowName.trim();
      const flowId = this.newFlow.flowId.trim();
      const payload = {
        id: generatedId,
        flowId,
        flowName
      };

      this.savingFlow = true;
      try {
        await this.createSignzyFlow(payload);
        this.$notify({
          type: 'success',
          title: 'Flow added',
          text: `${payload.flowName} is now available.`
        });
        this.closeFlowDialog();
        await this.fetchSignzyFlows();
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Unable to add flow',
          text: 'Please check the details and try again.'
        });
      } finally {
        this.savingFlow = false;
      }
    },
    async deleteFlow(flow) {
      const identifier = flow?.flowId || flow?.id;
      const id = flow?.id || flow?.flowId;
      if (!identifier || !id) {
        return;
      }
      this.deletingFlowId = identifier;
      try {
        await this.removeSignzyFlow({ id });
        this.$notify({
          type: 'success',
          title: 'Flow removed',
          text: `${flow.flowName || 'The flow'} has been removed.`
        });
        await this.fetchSignzyFlows();
      } catch (error) {
        this.$notify({
          type: 'error',
          title: 'Unable to remove flow',
          text: 'Please try again in a moment.'
        });
      } finally {
        this.deletingFlowId = null;
      }
    }
  }
};
</script>
